import { FlowPaymentTerms, PaymentMethod } from '~/types/flow';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { Account } from './account';
import { Category, Subcategory } from './category';
import { Company } from './company';
import { IdentityType } from './company-identity';
import { CountryIsoCode } from './country';

export const CounterpartResourceType = 'counterpart';

export interface Counterpart {
  address: string | null;
  account?: Account | null;
  addressCountry: CountryIsoCode | null;
  city: string | null;
  companyName: string | null;
  contactEmail: string | null;
  contactPerson: string | null;
  country: CountryIsoCode;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryCountry: CountryIsoCode | null;
  deliveryPostalCode: string | null;
  destinationCode: string | null;
  id: string;
  identityType: IdentityType | null;
  issuedCategory?: Category | null;
  issuedSubcategory?: Subcategory | null;
  kind: CounterpartKind;
  paymentDate: FlowPaymentTerms | null;
  paymentMethod: PaymentMethod | null;
  postalCode: string | null;
  provinceCode: string | null;
  receivedCategory?: Category | null;
  receivedSubcategory?: Subcategory | null;
  taxNumber: string | null;
  vatNumber: string | null;
  bankIdentifier: string | null;
  certifiedEmail: string | null;
  parent?: Counterpart | null;
}

export interface CounterpartWithChildren extends Counterpart {
  children: Counterpart[];
}

export enum CounterpartDirection {
  Client = 'CLIENT',
  Supplier = 'SUPPLIER',
}

export enum CounterpartKind {
  Trusted = 'TRUSTED',
  Virtual = 'VIRTUAL',
}

export interface CounterpartsMeta {
  page: MetaDataCursorPage;
}

export const CounterpartsQueryKeys = {
  all: ['Counterparts'],
  allVirtual: (companyId: string) => ['Counterparts', 'Virtual', { companyId }],
  companyInfinityList: (companyId: string, filters: CounterpartsFetchParameters) => [
    'Counterparts',
    { companyId },
    'Infinite',
    filters,
  ],
  search: (search: string) => ['Counterparts', { search }],
  single: (id?: string | null) => ['Counterparts', id],
  metadata: (filters: CounterpartsFetchParameters) => ['Counterparts', filters],
  suggested: (companyId: string, filters: CounterpartsFetchParameters) => [
    'Counterparts',
    companyId,
    'Suggested',
    filters,
  ],
};

export interface CounterpartPayload {
  address: string;
  addressCountry: string;
  city: string;
  companyName: string;
  identityType: IdentityType;
  country: CountryIsoCode;
  postalCode: string;
  taxNumber: string;
  vatNumber: string;
  destinationCode?: string;
  deliveryAddress?: string | null;
  deliveryCity?: string | null;
  deliveryCountry?: string | null;
  deliveryPostalCode?: string | null;
  bankIdentifier?: string | null;
  account?: string | null;
  contactPerson?: string | null;
  contactEmail?: string | null;
  issuedCategory?: string | null;
  issuedSubcategory?: string | null;
  receivedCategory?: string | null;
  receivedSubcategory?: string | null;
  paymentDate?: FlowPaymentTerms | null;
  certifiedEmail?: string | null;
}

export type CounterpartUpdatePayload = CounterpartPayload & {
  parent: Counterpart['id'] | null;
};

export type CounterpartCreatePayload = CounterpartPayload & { company: Company['id'] };

export interface CounterpartsFilters {
  direction: CounterpartDirection | null;
  search: string;
  type: IdentityType | null;
}

export interface CounterpartsFetchParameters {
  companyId: string;
  direction?: CounterpartDirection | null;
  bankIdentifier?: string | null;
  kind?: CounterpartKind;
  pageParam?: string;
  search?: string;
  type?: IdentityType | null;
  allowWithParent?: boolean;
  ids?: string[];
  excludeIds?: string[];
  withContactEmail?: boolean;
  hasDocuments?: boolean;
  supplierAccountId?: string;
  clientAccountId?: string;
}
